import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FileUpload from './FileUpload';
import Toast from '../common/Toast';
import { Document } from '../../types';
import { useUser } from '../../contexts/UserContext';
import { useOrganization, ORG_CONTEXT_CHANGED_EVENT } from '../../contexts/OrganizationContext';
import Select from 'react-select';

interface Department {
  id: string;
  name: string;
}

interface GroupedDocuments {
  [key: string]: {
    name: string;
    documents: Document[];
    count: number;
  };
}

function Documents() {
  const [personalDocuments, setPersonalDocuments] = useState<Document[]>([]);
  const [sharedWithMeDocuments, setSharedWithMeDocuments] = useState<Document[]>([]);
  const [sharedByMeDocuments, setSharedByMeDocuments] = useState<Document[]>([]);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ message: '', type: '' });
  const { id: currentUserId, loading: userLoading } = useUser();
  const { currentContext } = useOrganization();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());

  const fetchDocuments = async () => {
    try {
      const response = await axios.get('/api/user/documents');
      
      // Split documents into categories
      const personal: Document[] = [];
      const sharedWithMe: Document[] = [];
      const sharedByMe: Document[] = [];

      response.data.documents.forEach((doc: Document) => {
        if (doc.user_id === currentUserId && doc.uploaded_by_user_id === currentUserId) {
          // Documents uploaded by me for myself (personal or org-shared)
          personal.push(doc);
        } else if (doc.user_id === currentUserId) {
          // Documents uploaded by others for me
          sharedWithMe.push(doc);
        } else if (doc.uploaded_by_user_id === currentUserId) {
          // Documents I uploaded for others/departments/org
          sharedByMe.push(doc);
        }
      });

      setPersonalDocuments(personal);
      setSharedWithMeDocuments(sharedWithMe);
      setSharedByMeDocuments(sharedByMe);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to fetch documents');
    }
  };

  const fetchDepartments = async () => {
    if (!currentContext.organization_id) return;
    
    try {
      const response = await axios.get(`/api/organizations/${currentContext.organization_id}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    if (!userLoading) {
      fetchDocuments();
      fetchDepartments();
    }
  }, [userLoading, currentContext.organization_id]);

  // Listen for organization context changes
  useEffect(() => {
    const handleOrgContextChanged = () => {
      fetchDocuments();
      fetchDepartments();
    };

    window.addEventListener(ORG_CONTEXT_CHANGED_EVENT, handleOrgContextChanged);

    return () => {
      window.removeEventListener(ORG_CONTEXT_CHANGED_EVENT, handleOrgContextChanged);
    };
  }, []);

  const handlePermissionChange = async (documentId: string, newPermission: string) => {
    try {
      await axios.patch(`/api/user/documents/${documentId}/permission`, {
        permission_type: newPermission
      });
      fetchDocuments();
      setToast({ message: 'Document permission updated successfully', type: 'success' });
    } catch (error) {
      console.error('Error updating document permission:', error);
      setToast({ message: 'Failed to update document permission', type: 'error' });
    }
  };

  const handleDepartmentChange = async (documentId: string, departmentId: string | null) => {
    try {
      await axios.patch(`/api/user/documents/${documentId}/department`, {
        department_id: departmentId
      });
      fetchDocuments();
      setToast({ message: 'Document department updated successfully', type: 'success' });
    } catch (error) {
      console.error('Error updating document department:', error);
      setToast({ message: 'Failed to update document department', type: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`/api/user/documents/${id}`);
      fetchDocuments();
      setToast({ message: 'Document deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error deleting document:', error);
      setToast({ message: 'Failed to delete document', type: 'error' });
    }
  };

  const handleUploadSuccess = () => {
    fetchDocuments();
    setToast({ message: 'File uploaded successfully', type: 'success' });
  };

  const toggleDepartmentExpansion = (departmentKey: string) => {
    const newExpanded = new Set(expandedDepartments);
    if (newExpanded.has(departmentKey)) {
      newExpanded.delete(departmentKey);
    } else {
      newExpanded.add(departmentKey);
    }
    setExpandedDepartments(newExpanded);
  };

  const groupDocumentsByDepartment = (documents: Document[]): GroupedDocuments => {
    const grouped: GroupedDocuments = {};
    
    documents.forEach(doc => {
      let key: string;
      let name: string;
      
      if (doc.permission_type === 'private') {
        key = 'private';
        name = 'Private Documents';
      } else if (doc.department_id && doc.department_name) {
        key = `dept_${doc.department_id}`;
        name = `${doc.department_name} Department`;
      } else {
        key = 'organization';
        name = 'Organization-wide Documents';
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          name,
          documents: [],
          count: 0
        };
      }
      
      grouped[key].documents.push(doc);
      grouped[key].count++;
    });
    
    return grouped;
  };

  const DocumentList = ({ documents, isSharedWithMe = false }) => (
    <ul className="bg-white shadow-md rounded-lg divide-y divide-gray-200">
      {documents.map((doc: Document) => (
        <li key={doc.id} className="flex justify-between items-center p-4 hover:bg-gray-50">
          <div className="flex-grow">
            <span className="font-medium">{doc.file_name}</span>
            <span className="ml-2 text-sm text-gray-500">({doc.file_type})</span>
            
            {/* Show who uploaded the document if shared with me */}
            {isSharedWithMe && doc.uploaded_by_name && (
              <span className="ml-2 text-xs text-gray-500">
                Uploaded by: {doc.uploaded_by_name}
              </span>
            )}

            {/* Show department information */}
            {doc.permission_type === 'organization' && (
              <div className="mt-1">
                <span className="text-xs text-gray-500">
                  {doc.department_name 
                    ? `Department: ${doc.department_name}`
                    : 'Organization-wide'}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {currentContext.organization_id && !isSharedWithMe && doc.permission_type === 'organization' && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Department:</span>
                <Select
                  value={doc.department_id 
                    ? { value: doc.department_id, label: doc.department_name || 'Unknown' }
                    : { value: '', label: 'Organization-wide' }
                  }
                  onChange={(option) => handleDepartmentChange(doc.id, option?.value || null)}
                  options={[
                    { value: '', label: 'Organization-wide' },
                    ...departments.map(dept => ({ value: dept.id, label: dept.name }))
                  ]}
                  className="w-40 text-xs"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '28px',
                      fontSize: '12px'
                    }),
                    option: (base) => ({
                      ...base,
                      fontSize: '12px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontSize: '12px'
                    })
                  }}
                />
              </div>
            )}
            {currentContext.organization_id && !isSharedWithMe && (
              <select
                value={doc.permission_type}
                onChange={(e) => handlePermissionChange(doc.id, e.target.value)}
                className="border rounded px-2 py-1 text-sm bg-white focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="private">Private</option>
                <option value="organization">Organization</option>
              </select>
            )}
            {!isSharedWithMe && (
              <button
                onClick={() => handleDelete(doc.id)}
                className="px-3 py-1 rounded text-sm text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Delete
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );

  const GroupedDocumentList = ({ documents, isSharedWithMe = false }) => {
    const grouped = groupDocumentsByDepartment(documents);
    
    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([key, group]) => (
          <div key={key} className="bg-white shadow-md rounded-lg">
            <button
              onClick={() => toggleDepartmentExpansion(key)}
              className="w-full p-4 text-left hover:bg-gray-50 flex justify-between items-center"
            >
              <div className="flex items-center">
                <span className="font-semibold">{group.name}</span>
                <span className="ml-2 text-sm text-gray-500">({group.count} documents)</span>
              </div>
              <span className="text-gray-400">
                {expandedDepartments.has(key) ? '▼' : '▶'}
              </span>
            </button>
            {expandedDepartments.has(key) && (
              <div className="border-t">
                <DocumentList documents={group.documents} isSharedWithMe={isSharedWithMe} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">Document Management</h1>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">Upload New Document</h2>
        <div className="bg-gray-100 p-6 rounded-lg">
          <FileUpload onUploadSuccess={handleUploadSuccess} setToast={setToast} />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">My Documents</h2>
        {error && <p className="text-red-500 mb-4">{error}</p>}
        {personalDocuments.length === 0 ? (
          <p className="text-gray-600">No personal documents uploaded yet.</p>
        ) : (
          <GroupedDocumentList documents={personalDocuments} />
        )}
      </section>

      {sharedWithMeDocuments.length > 0 && (
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Documents Shared With Me</h2>
          <GroupedDocumentList documents={sharedWithMeDocuments} isSharedWithMe={true} />
        </section>
      )}

      {sharedByMeDocuments.length > 0 && (
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Documents I've Shared</h2>
          <GroupedDocumentList documents={sharedByMeDocuments} />
        </section>
      )}

      {toast.message && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast({ message: '', type: '' })} 
        />
      )}
    </div>
  );
}

export default Documents;